import { graphqlRequest } from "../apiClient";

const PLATFORM_TENANTS = `
  query PlatformTenants($filter: TenantFilter) {
    platformTenants(filter: $filter) {
      tenant_id
      name
      slug
      plan
      status
      email
      is_demo
      arca_validation_enabled
      copilot_enabled
      created_at
    }
  }
`;

const PLATFORM_TENANT = `
  query PlatformTenant($tenantId: ID!) {
    platformTenant(tenant_id: $tenantId) {
      tenant_id
      name
      slug
      plan
      status
      email
      is_demo
      arca_validation_enabled
      copilot_enabled
      created_at
      fiscal_cuit
      fiscal_legal_name
      contract_terms {
        tenant_id
        monthly_fee_ars
        implementation_fee_ars
        implementation_fee_split
        billing_currency
        fx_reference
        fair_use_cap_docs_monthly
        copilot_included
        arca_validation_included
        quality_review_clause_active_from
        quality_review_clause_status
        contract_annex_ref
        notes
        updated_at
      }
      commercial_plan {
        tenant_id
        arca_enabled
        copilot_enabled
        max_documents_monthly
        fallback_tokens_model
        fallback_tokens_included
        monthly_base_fee_ars
        currency
        effective_from
        updated_at
      }
      usage_summary {
        tenant_id
        period
        documents_processed
        documents_limit
        usage_pct
        plan_exceeded
        fallback_tokens_used
        fallback_tokens_included
        copilot_input_tokens
        copilot_output_tokens
      }
      billing_records {
        record_id
        record_type
        period
        amount_ars
        status
        due_date
        paid_at
        notes
        line_items {
          code
          description
          quantity
          unit_amount_ars
          amount_ars
        }
        created_at
      }
    }
  }
`;

const PLATFORM_PENDING = `
  query PlatformPendingTenants {
    platformPendingTenants {
      tenant_id
      name
      status
      created_at
    }
  }
`;

const USAGE_SUMMARY = `
  query PlatformTenantUsageSummary($tenantId: ID!, $period: String) {
    platformTenantUsageSummary(tenant_id: $tenantId, period: $period) {
      tenant_id
      period
      documents_processed
      documents_limit
      usage_pct
      plan_exceeded
      fallback_tokens_used
      fallback_tokens_included
      copilot_input_tokens
      copilot_output_tokens
    }
  }
`;

const USAGE_HISTORY = `
  query PlatformTenantUsageHistory($tenantId: ID!, $dateFrom: String!, $dateTo: String!) {
    platformTenantUsageHistory(tenant_id: $tenantId, date_from: $dateFrom, date_to: $dateTo) {
      period
      documents_processed
      fallback_tokens_used
      copilot_input_tokens
      copilot_output_tokens
    }
  }
`;

const APPROVE = `
  mutation PlatformApproveTenant($tenantId: ID!) {
    platformApproveTenant(tenant_id: $tenantId) {
      tenant_id
      status
    }
  }
`;

const REJECT = `
  mutation PlatformRejectTenant($input: RejectTenantInput!) {
    platformRejectTenant(input: $input) {
      tenant_id
      status
    }
  }
`;

const DELETE_TENANT = `
  mutation PlatformDeleteTenant($input: DeleteTenantInput!) {
    platformDeleteTenant(input: $input) {
      tenant_id
      deleted
    }
  }
`;

const SUSPEND = `
  mutation PlatformSuspendTenant($input: SuspendTenantInput!) {
    platformSuspendTenant(input: $input) {
      tenant_id
      status
    }
  }
`;

const REACTIVATE = `
  mutation PlatformReactivateTenant($tenantId: ID!) {
    platformReactivateTenant(tenant_id: $tenantId) {
      tenant_id
      status
    }
  }
`;

const FEATURE_OVERRIDE = `
  mutation PlatformSetTenantFeatureOverride($input: TenantFeatureOverrideInput!) {
    platformSetTenantFeatureOverride(input: $input) {
      tenant_id
      feature
      enabled
      arca_validation_enabled
      copilot_enabled
    }
  }
`;

const SET_CONTRACT = `
  mutation PlatformSetContractTerms($input: TenantContractTermsInput!) {
    platformSetContractTerms(input: $input) {
      tenant_id
      monthly_fee_ars
      fair_use_cap_docs_monthly
      quality_review_clause_active_from
      contract_annex_ref
      updated_at
    }
  }
`;

const SET_PLAN = `
  mutation PlatformSetTenantPlan($input: SetTenantPlanInput!) {
    platformSetTenantPlan(input: $input) {
      tenant_id
      arca_enabled
      copilot_enabled
      max_documents_monthly
      fallback_tokens_model
      fallback_tokens_included
      monthly_base_fee_ars
      updated_at
    }
  }
`;

const GENERATE_INVOICE = `
  mutation PlatformGenerateInvoiceDraft($tenantId: ID!, $period: String!) {
    platformGenerateInvoiceDraft(tenant_id: $tenantId, period: $period) {
      record_id
      amount_ars
      status
      line_items {
        code
        description
        amount_ars
      }
    }
  }
`;

const CREATE_BILLING = `
  mutation PlatformCreateBillingRecord($input: BillingRecordInput!) {
    platformCreateBillingRecord(input: $input) {
      record_id
      status
      amount_ars
    }
  }
`;

const UPDATE_BILLING = `
  mutation PlatformUpdateBillingRecord($input: BillingRecordUpdateInput!) {
    platformUpdateBillingRecord(input: $input) {
      record_id
      status
      paid_at
    }
  }
`;

export async function fetchPlatformTenants(filter) {
  const data = await graphqlRequest(PLATFORM_TENANTS, { filter: filter || null });
  return data?.platformTenants || [];
}

export async function fetchPlatformTenant(tenantId) {
  const data = await graphqlRequest(PLATFORM_TENANT, { tenantId });
  return data?.platformTenant;
}

export async function fetchPlatformPendingTenants() {
  const data = await graphqlRequest(PLATFORM_PENDING);
  return data?.platformPendingTenants || [];
}

export async function fetchTenantUsageSummary(tenantId, period) {
  const data = await graphqlRequest(USAGE_SUMMARY, { tenantId, period: period || null });
  return data?.platformTenantUsageSummary;
}

export async function fetchTenantUsageHistory(tenantId, dateFrom, dateTo) {
  const data = await graphqlRequest(USAGE_HISTORY, { tenantId, dateFrom, dateTo });
  return data?.platformTenantUsageHistory || [];
}

export async function approveTenant(tenantId) {
  const data = await graphqlRequest(APPROVE, { tenantId });
  return data?.platformApproveTenant;
}

export async function rejectTenant(tenantId, reason, category = "other") {
  const data = await graphqlRequest(REJECT, {
    input: { tenant_id: tenantId, reason, category },
  });
  return data?.platformRejectTenant;
}

export async function deleteTenant(tenantId, confirmationSlug, reason = "") {
  const data = await graphqlRequest(DELETE_TENANT, {
    input: { tenant_id: tenantId, confirmation_slug: confirmationSlug, reason },
  });
  return data?.platformDeleteTenant;
}

export async function suspendTenant(tenantId, reason = "other", note = "") {
  const data = await graphqlRequest(SUSPEND, {
    input: { tenant_id: tenantId, reason, note, notify_contact: false },
  });
  return data?.platformSuspendTenant;
}

export async function reactivateTenant(tenantId) {
  const data = await graphqlRequest(REACTIVATE, { tenantId });
  return data?.platformReactivateTenant;
}

export async function setTenantFeatureOverride(tenantId, feature, enabled) {
  const data = await graphqlRequest(FEATURE_OVERRIDE, {
    input: { tenant_id: tenantId, feature, enabled },
  });
  return data?.platformSetTenantFeatureOverride;
}

export async function setContractTerms(input) {
  const data = await graphqlRequest(SET_CONTRACT, { input });
  return data?.platformSetContractTerms;
}

export async function setTenantPlan(input) {
  const data = await graphqlRequest(SET_PLAN, { input });
  return data?.platformSetTenantPlan;
}

export async function generateInvoiceDraft(tenantId, period) {
  const data = await graphqlRequest(GENERATE_INVOICE, { tenantId, period });
  return data?.platformGenerateInvoiceDraft;
}

export async function createBillingRecord(input) {
  const data = await graphqlRequest(CREATE_BILLING, { input });
  return data?.platformCreateBillingRecord;
}

export async function updateBillingRecord(input) {
  const data = await graphqlRequest(UPDATE_BILLING, { input });
  return data?.platformUpdateBillingRecord;
}

export function currentPeriod() {
  return new Date().toISOString().slice(0, 7);
}

export function previousPeriod(period) {
  const [year, month] = period.split("-").map(Number);
  if (month === 1) return `${year - 1}-12`;
  return `${year}-${String(month - 1).padStart(2, "0")}`;
}

const PLAN_CATALOG = `
  query PlatformPlanCatalog {
    platformPlanCatalog {
      plan_id
      name
      status
      expiry_status
      days_until_expiry
      arca_enabled
      copilot_enabled
      max_documents_monthly
      fallback_tokens_model
      fallback_tokens_included
      monthly_base_fee_ars
      valid_from
      valid_until
    }
  }
`;

const CREATE_PLAN = `
  mutation PlatformCreatePlanCatalog($input: CreatePlanCatalogInput!) {
    platformCreatePlanCatalog(input: $input) {
      plan_id
      name
      status
    }
  }
`;

const UPDATE_PLAN_STATUS = `
  mutation PlatformUpdatePlanCatalogStatus($planId: ID!, $status: String!) {
    platformUpdatePlanCatalogStatus(plan_id: $planId, status: $status) {
      plan_id
      status
    }
  }
`;

const ASSIGN_PLAN = `
  mutation PlatformAssignPlanToTenant($input: AssignPlanToTenantInput!) {
    platformAssignPlanToTenant(input: $input) {
      tenant_id
      catalog_plan_id
    }
  }
`;

export async function fetchPlanCatalog() {
  const data = await graphqlRequest(PLAN_CATALOG);
  return data?.platformPlanCatalog || [];
}

export async function createPlanCatalog(input) {
  const data = await graphqlRequest(CREATE_PLAN, { input });
  return data?.platformCreatePlanCatalog;
}

export async function updatePlanCatalogStatus(planId, status) {
  const data = await graphqlRequest(UPDATE_PLAN_STATUS, { planId, status });
  return data?.platformUpdatePlanCatalogStatus;
}

export async function assignPlanToTenant(tenantId, planId) {
  const data = await graphqlRequest(ASSIGN_PLAN, {
    input: { tenant_id: tenantId, plan_id: planId },
  });
  return data?.platformAssignPlanToTenant;
}
